// File a document — or a folder's documents — into a case of the file plan,
// or link a folder to a case (its newly signed documents then file
// themselves, where the Records policy says so). Filing makes a
// record: the document can't change from then on, and the dialog says so.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { records } from '../lib/api.js';
import { periodLabel } from '../lib/retention.js';

// mode: 'file' (one document) | 'folder' (its documents) | 'link' (the folder itself).
// `node`: the tree's row (`{uuid, name}`) — a document's uuid for 'file', a
// folder's for 'folder' and 'link'.
export function FileToCaseDialog({ open, onOpenChange, node, mode = 'file', onDone }) {
  const { t } = useTranslation('records');
  const [cases, setCases] = useState(null);
  const [q, setQ] = useState('');
  const [chosen, setChosen] = useState(null);
  const [scope, setScope] = useState('signed');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setCases(null);
    setQ('');
    setChosen(null);
    setScope('signed');
    setResult(null);
    records.openCases().then((res) => {
      if (cancelled) return;
      if (res.success) setCases(res.response);
      else {
        toast.error(res.message);
        setCases([]);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (cases || []).filter((c) => !needle || `${c.case_index} ${c.title} ${c.unit || ''}`.toLowerCase().includes(needle));
  }, [cases, q]);

  const submit = async () => {
    setBusy(true);
    let res;
    if (mode === 'link') res = await records.linkFolder(chosen.uuid, node.uuid);
    else if (mode === 'folder') res = await records.fileFolder(chosen.uuid, node.uuid, scope);
    else res = await records.fileDocument(chosen.uuid, node.uuid);
    setBusy(false);
    if (!res.success) {
      toast.error(res.message);
      return;
    }
    if (mode === 'folder') {
      setResult(res.response);
      if (res.response.filed.length) onDone?.();
      return;
    }
    toast.success(t(mode === 'link' ? 'filing.linked' : 'filing.filed', { index: chosen.case_index }));
    onDone?.();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t(`filing.${mode}Title`, { name: node?.name })}</DialogTitle>
          <DialogDescription>{t(`filing.${mode}Description`)}</DialogDescription>
        </DialogHeader>
        {result ? (
          <div className="space-y-2 text-sm" data-testid="filing-result">
            <p>{t('filing.folderDone', { filed: result.filed.length, skipped: result.skipped.length })}</p>
            {result.skipped.length > 0 && (
              <ul className="max-h-48 space-y-1 overflow-y-auto rounded-md border p-2 text-xs text-muted-foreground">
                {result.skipped.map((s) => (
                  <li key={s.uuid}>{s.name} — {t(`filing.reasons.${s.reason}`, { defaultValue: s.reason })}</li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('filing.searchCase')} aria-label={t('filing.searchCase')} />
            {!cases ? (
              <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
            ) : shown.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground" data-testid="filing-no-cases">{t('filing.noCases')}</p>
            ) : (
              <ul role="listbox" aria-label={t('filing.case')} className="max-h-64 divide-y overflow-y-auto rounded-md border">
                {shown.map((c) => (
                  <li
                    key={c.uuid}
                    role="option"
                    aria-selected={chosen?.uuid === c.uuid}
                    tabIndex={0}
                    onClick={() => setChosen(c)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setChosen(c); } }}
                    className={cn('flex cursor-pointer items-start gap-3 px-3 py-2 text-sm hover:bg-muted', chosen?.uuid === c.uuid && 'bg-brand/10')}
                  >
                    <span className="w-16 shrink-0 font-mono text-xs text-muted-foreground">{c.case_index}</span>
                    <span className="flex-1">
                      {c.title}
                      <span className="block text-xs text-muted-foreground">{c.year}{c.unit ? ` · ${c.unit}` : ''}</span>
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground">{periodLabel(c.retention, t)}</span>
                  </li>
                ))}
              </ul>
            )}
            {mode === 'folder' && (
              <fieldset className="space-y-1 text-sm">
                <legend className="mb-1 font-medium">{t('filing.scope')}</legend>
                {['signed', 'all'].map((value) => (
                  <label key={value} className="flex items-center gap-2">
                    <input type="radio" name="filing-scope" value={value} checked={scope === value} onChange={() => setScope(value)} />
                    {t(`filing.scopes.${value}`)}
                  </label>
                ))}
              </fieldset>
            )}
            {mode !== 'link' && (
              <p className="flex items-start gap-2 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-2 text-xs text-amber-800 dark:text-amber-300">
                <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {t('filing.lockWarning')}
              </p>
            )}
          </div>
        )}
        <DialogFooter>
          {result ? (
            <Button onClick={() => onOpenChange(false)}>{t('filing.done')}</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>{t('actions.cancel')}</Button>
              <Button onClick={submit} disabled={busy || !chosen} data-testid="filing-submit">
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t(mode === 'link' ? 'filing.link' : 'filing.submit')}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
